import { useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * El tema de Compite (spec 310): Inter, radios amables, sombras suaves y
 * tamaños de letra reales. Los colores NO cambian: son los de la app. Se
 * aplica SÓLO a lo que envuelve; el resto de la app —incluidos el sidebar y la
 * barra superior— queda como siempre.
 *
 * Va adentro del `AppLayout`, envolviendo el contenido de la página:
 *
 *   <AppLayout>
 *     <TemaCompite>…</TemaCompite>
 *   </AppLayout>
 *
 * Los diálogos, menús y popovers se dibujan en un portal, fuera de este árbol.
 * Mientras esto está montado, `<html>` lleva `data-tema-compite` y el CSS se lo
 * pasa a los portales (`body > :not(#root)`), no al resto de la app.
 */
let montados = 0;

/**
 * Marca `<html data-tema-compite>` mientras `activo`, para que los portales
 * (diálogos, menús, popovers) también tomen el tema. Lo usan esta envoltura y
 * el marco fijo de la app (rediseño del 18-oct: el tema vale para toda la app).
 */
export function useTemaEnElDocumento(activo = true) {
  useEffect(() => {
    if (!activo) return;
    // Contador y no un booleano: al navegar entre dos pantallas, la nueva se
    // monta antes de que la vieja se desmonte.
    montados += 1;
    document.documentElement.setAttribute('data-tema-compite', '');
    return () => {
      montados -= 1;
      if (montados === 0) document.documentElement.removeAttribute('data-tema-compite');
    };
  }, [activo]);
}

export function TemaCompite({ children, className }: { children: ReactNode; className?: string }) {
  useTemaEnElDocumento();

  return (
    /*
      `-mb-44 pb-44`: el `<main>` del AppLayout deja 176 px abajo para los
      botones flotantes, y ese relleno quedaba fuera del tema (una franja de otro
      color al final del scroll). El envoltorio lo cubre y conserva el espacio.
    */
    <div
      data-testid="tema-compite"
      className={cn(
        // Transparente: se ve el lienzo de la app detrás de las tarjetas (18-oct).
        'tema-compite -mb-44 min-h-screen pb-44 text-sm text-foreground',
        className
      )}
    >
      {children}
    </div>
  );
}
